import type { ReactNode } from 'react';
import { ClickableIcon } from '@zenid/web';
import { css } from '../../../../../../styled-system/css';
import { flex } from '../../../../../../styled-system/patterns';
import { EnrollmentDetail } from '#/components/EnrollmentDetail';
import { useCopyToClipboard } from '#/features/myPage/hooks/useCopyToClipboard';
import type { components } from '#/types/api/zenid-mypage-api';
import { PROFILE_FIELD_NAME } from '#/utils/constants';

type OrganizationProps = { membership: components['schemas']['Models.Membership'] };

export function Organization({ membership }: OrganizationProps) {
  return (
    <div
      className={flex({
        background: '{colors.background.white}',
        flexDirection: 'column',
        padding: '{spacing.8} {spacing.16} {spacing.12}',
        _last: {
          borderRadius: '0 0 {radii.16} {radii.16}',
        },
        sm: { padding: '{spacing.8} {spacing.32} {spacing.20}' },
      })}
    >
      <Title
        organizationName={membership.organization.name}
        organizationDisplayName={membership.organization.displayName}
      ></Title>
      <Detail membership={membership} />
    </div>
  );
}

const organizationLogoStyle = css({
  width: '24px',
  height: '24px',
});

function Title({
  organizationName,
  organizationDisplayName,
}: {
  organizationName: string;
  organizationDisplayName: string;
}) {
  return (
    <div
      className={flex({
        width: '100%',
        borderBottom: '1px solid {colors.border.dark}',
        padding: '{spacing.12} 0',
      })}
    >
      <h4
        className={flex({
          textStyle: 'headding.s.bold',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '{spacing.8}',
        })}
      >
        {organizationName === 'nfdw-edu/zen-university' && (
          <img
            src={`${import.meta.env.VITE_ASSETS_URL}/assets/images/zen_univ_logo.png`}
            className={organizationLogoStyle}
            alt="ZEN大学"
          />
        )}
        {organizationName === 'kddw-edu/n-highschool' && (
          <img
            src={`${import.meta.env.VITE_ASSETS_URL}/assets/images/n_school_logo.png`}
            className={organizationLogoStyle}
            alt="N高等学校"
          />
        )}
        {organizationDisplayName}
      </h4>
    </div>
  );
}

function ListItem({ title, children }: { title: string; children: ReactNode }) {
  return (
    <li
      className={flex({
        padding: '{spacing.12} 0',
        gap: '{spacing.8}',
        alignItems: 'flex-start',
        alignSelf: 'stretch',
        borderBottom: '1px solid {colors.border.light}',
        _last: {
          borderBottom: 'none',
        },
        sm: { padding: '{spacing.12} 0' },
      })}
    >
      <div className={flex({ width: '100%', flexDirection: 'column', sm: { flexDirection: 'row' } })}>
        <p
          className={flex({
            width: '100%',
            maxWidth: '311px',
            alignItems: 'center',
            color: '{colors.text.main}',
            textStyle: 'body.s.defalut',
            sm: { maxWidth: '140px' },
          })}
        >
          {title}
        </p>
        <div className={flex({ width: '100%', alignItems: 'center', gap: '{spacing.8}', alignSelf: 'stretch' })}>
          {children}
        </div>
      </div>
    </li>
  );
}

function Detail({
  membership: { profile, joinedAt, leftAt },
}: {
  membership: components['schemas']['Models.Membership'];
}) {
  const { copy } = useCopyToClipboard();

  return (
    <ul
      className={flex({
        width: '100%',
        flexDirection: 'column',
      })}
    >
      {profile?.fields.map((field) => {
        // https://dwango.slack.com/archives/C06UHFALZRQ/p1722319698547869?thread_ts=1722318875.833079&cid=C06UHFALZRQ
        if (
          field.fieldName === PROFILE_FIELD_NAME.STUDENT_NUMBER ||
          field.fieldName === PROFILE_FIELD_NAME.EMPLOYEE_NUMBER
        ) {
          return (
            <ListItem key={field.fieldName} title={field.displayName}>
              <div className={flex({ width: '100%', alignItems: 'center', gap: '{spacing.8}', alignSelf: 'stretch' })}>
                <p className={css({ textStyle: 'body.m.default', color: '{colors.text.main}' })}>{`${field.value}`}</p>
                <ClickableIcon
                  name="content_copy"
                  color="colors.text.sub"
                  size="s"
                  onClick={() => copy(`${field.value}`)}
                />
              </div>
            </ListItem>
          );
        }

        if (field.fieldName === PROFILE_FIELD_NAME.APPLICATION_NUMBER) {
          // 出願番号(applicationNumber)の場合は表示しない
          return null;
        }

        return (
          <ListItem key={field.fieldName} title={field.displayName}>
            <p className={css({ textStyle: 'body.m.default', color: '{colors.text.main}' })}>{`${field.value}`}</p>
          </ListItem>
        );
      })}
      <ListItem title="在籍期間">
        <EnrollmentDetail
          period={{
            start: joinedAt,
            end: leftAt,
          }}
          // TODO: InternalAPIのレスポンスにプロパティ追加が必要。金子さんとは調整済みなのでinternal-api側の修正が完了したら実装する。
          explanatoryText=""
          // TODO: 開学スコープまでは履歴の情報は取得できない(そもそもどのように履歴を入力してもらうかが未確定)ため、開学スコープ以降に対応を行なっていく。
          // https://dwango.slack.com/archives/C0621SRMLMV/p1722396834337289
          attachedItems={[]}
        />
      </ListItem>
    </ul>
  );
}
